import { createRoot } from 'react-dom/client'
import './index.css'
import React from 'react'
import { initializeBasicSecurity } from './utils/simplified-security'
import { logger } from './services/logger'
import { registerServiceWorker } from './utils/serviceWorker'
import { initializeAdminFixes } from './utils/admin-fix'
import App from './App'

// Import the client-side admin fix
import './utils/client-side-admin-fix'

// Initialize logging and security
logger.info('Application initializing');

// Initialize basic security features
initializeBasicSecurity();

// Initialize admin fixes for navigation issues
initializeAdminFixes().catch(error => {
  logger.error('Failed to initialize admin fixes', { error });
});

// Register service worker for offline support
registerServiceWorker({
  onUpdate: (registration) => {
    logger.info('Service worker updated', { registration });
  },
  onSuccess: (registration) => {
    logger.info('Service worker registered', { registration });
  },
  onError: (error) => {
    logger.error('Service worker registration failed', { error });
  }
});

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element not found');
}

const root = createRoot(container);

// Error boundary for the entire application
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error('Application error boundary caught error', { error, errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center p-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Something went wrong
            </h1>
            <p className="text-gray-600 mb-4">
              We're sorry, but something unexpected happened.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Log app start
logger.info('React app mounting');

root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);
